import React from 'react'
import { ReactComponent as InfoIcon } from '../../assets/images/info-icon.svg'

/**
 * A titled group of fields in the deploy wizard's configure step.
 *
 * Replaces the accordion this step used to be, and the reason is what the step
 * now says on its own heading: "Review what we filled in". Almost every field
 * here arrives pre-filled from the source, and a review you have to open five
 * drawers to perform is one nobody performs — the collapsed state hid exactly
 * the values the screen exists to show. Everything is visible; the rule and
 * the label do the grouping the disclosure arrow used to.
 *
 * `CollapsibleComponent` is untouched and still right where a section is
 * genuinely optional reading — this is a different job, so it is a different
 * component rather than a flag on that one.
 */
const FormSection = ({ title, children, infoText, badge, summary }: {
  title: string
  children: React.ReactNode
  infoText?: string
  /** A short state word — "auto-detected", "optional". Carries color, so keep
   * it to things that are true about the section rather than about a field. */
  badge?: React.ReactNode
  /** One line of plain text after the badge. Explains the badge; never a
   * second heading. */
  summary?: string
}) => (
  <section className="pt-8 first:pt-0">
    <div className="flex items-center gap-3.5 mb-5">
      <h3 className="m-0 text-[0.6875rem] font-bold uppercase tracking-[0.09em] text-text-faint whitespace-nowrap">
        {title}
      </h3>
      {infoText && (
        <span title={infoText} className="flex-none -ml-1.5">
          <InfoIcon className="opacity-60" />
        </span>
      )}
      <span aria-hidden="true" className="flex-1 h-px bg-card-line"></span>
      {badge}
      {summary && <span className="text-xs leading-140 text-text-faint whitespace-nowrap max-sm:hidden">{summary}</span>}
    </div>
    {children}
  </section>
)

export default FormSection
